/**
 * AnatomyService — the working surface of the Anatomy ("Acts API") integration, served by
 * `CCMS.Api.AnatomyIntegration` (gateway prefix `anatomy`).
 *
 * Routes — /api/anatomy   (Anatomy.View to read, Anatomy.Manage to write)
 *   - GET  /status                          — enabled / configured / last test / 24h counts
 *   - POST /people/check                    — is this person in Anatomy? → anatomyUserId or 0
 *   - GET  /people/find?email=|mobile=|name=&surname=|memberId=|anatomyUserId= — id by whatever is known (member links first)
 *   - GET  /follow-up-types?refresh=&q=&name=&campus= — cached 10 min server-side; filters run on the cache
 *   - GET  /follow-up-types/{id}
 *   - POST /follow-ups                      — create a follow-up (queued in Anatomy if userId 0)
 *   - GET  /meetings?refresh=&q=&topic=&venue=&type=&from=&to=&date=&availableOnly= — upcoming meetings with capacity
 *   - GET  /meetings/next?topic=&venue=… — the soonest matching meeting that has not started (404 → null)
 *   - GET  /meetings/{id}
 *   - POST /meetings/{id}/register          — register a person (refused if full)
 *   - GET  /logs?function=&failuresOnly=&take=
 *   - GET  /dashboard?refresh=              — everything the Dashboard page shows, in one call
 *   - GET  /follow-ups?take=                — follow-ups sent, newest first
 *   - GET  /registrations?take=             — meeting registrations sent, newest first
 *   - GET  /members/search?term=&limit=     — CCMS members with their Anatomy link (via MemberManagement)
 *   - GET  /members/{id}/link               — one member's link (cached)
 *   - POST /members/{id}/link               — set (or clear with 0) a member's Anatomy id
 *
 * Pass `memberId` on check / follow-up / register and the service remembers a successful match
 * on the CCMS member, so later calls for that person never ask Anatomy who they are.
 *
 * Every write is logged server-side; the returned `logId` points at that row.
 */
import { BaseService } from '../base/BaseService';
import { CCMSError } from '../../models/common/CCMSError';
export class AnatomyService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? AnatomyService.DEFAULT_SERVICE_PATH,
            basePath: AnatomyService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    async getStatus() {
        const r = await this.getStatusRaw();
        if (!r.data)
            throw new Error(r.message ?? 'Could not read Anatomy status');
        return r.data;
    }
    getStatusRaw() {
        return this.http.get(this.buildUrl(this.getEndpoint('status', 'status')));
    }
    /** Always the first step before a follow-up or registration. */
    async checkPerson(request) {
        const r = await this.checkPersonRaw(request);
        if (!r.data)
            throw new Error(r.message ?? 'Check failed');
        return r.data;
    }
    checkPersonRaw(request) {
        return this.http.post(this.buildUrl(this.getEndpoint('check', 'people/check')), request);
    }
    /** Find a person's Anatomy id by email, mobile, name + surname, CCMS member id or Anatomy id. */
    async findPerson(request) {
        const r = await this.findPersonRaw(request);
        if (!r.data)
            throw new Error(r.message ?? 'Find failed');
        return r.data;
    }
    findPersonRaw(request) {
        return this.http.get(this.buildUrlWithParams(this.getEndpoint('people-find', 'people/find'), {
            email: request.email || undefined, mobile: request.mobile || undefined, name: request.name || undefined, surname: request.surname || undefined,
            memberId: request.memberId || undefined, anatomyUserId: request.anatomyUserId || undefined,
            askAnatomy: request.askAnatomy === false ? false : undefined,
        }));
    }
    async getFollowUpTypes(refresh = false, filter = {}) {
        const r = await this.getFollowUpTypesRaw(refresh, filter);
        return r.data ?? [];
    }
    getFollowUpTypesRaw(refresh = false, filter = {}) {
        return this.http.get(this.buildUrlWithParams(this.getEndpoint('follow-up-types', 'follow-up-types'), {
            refresh: refresh || undefined, q: filter.q || undefined, name: filter.name || undefined, campus: filter.campus || undefined,
        }));
    }
    async getFollowUpType(typeId) {
        const r = await this.http.get(this.buildUrl(this.getEndpoint('follow-up-type', `follow-up-types/${typeId}`)));
        return r.data ?? null;
    }
    async createFollowUp(request) {
        const r = await this.createFollowUpRaw(request);
        if (!r.data)
            throw new Error(r.message ?? 'Follow-up failed');
        return r.data;
    }
    createFollowUpRaw(request) {
        return this.http.post(this.buildUrl(this.getEndpoint('follow-ups', 'follow-ups')), request);
    }
    async getMeetings(refresh = false, filter = {}) {
        const r = await this.getMeetingsRaw(refresh, filter);
        return r.data ?? [];
    }
    getMeetingsRaw(refresh = false, filter = {}) {
        return this.http.get(this.buildUrlWithParams(this.getEndpoint('meetings', 'meetings'), {
            refresh: refresh || undefined, q: filter.q || undefined, topic: filter.topic || undefined,
            venue: filter.venue || filter.location || undefined, type: filter.type || undefined,
            from: filter.from || undefined, to: filter.to || undefined, date: filter.date || undefined,
            availableOnly: filter.availableOnly || undefined,
        }));
    }
    /** The next upcoming meeting for a topic and/or venue — the soonest that has not started yet; null when none. */
    async getNextMeeting(filter = {}, includeStarted = false) {
        try {
            const r = await this.http.get(this.buildUrlWithParams(this.getEndpoint('meeting-next', 'meetings/next'), {
                q: filter.q || undefined, topic: filter.topic || undefined, venue: filter.venue || filter.location || undefined,
                type: filter.type || undefined, from: filter.from || undefined, to: filter.to || undefined, date: filter.date || undefined,
                availableOnly: filter.availableOnly || undefined, includeStarted: includeStarted || undefined,
            }));
            return r.data ?? null;
        }
        catch (e) {
            if (e instanceof CCMSError && e.status === 404)
                return null;
            throw e;
        }
    }
    async getMeeting(meetingId) {
        const r = await this.http.get(this.buildUrl(this.getEndpoint('meeting', `meetings/${meetingId}`)));
        return r.data ?? null;
    }
    async registerForMeeting(meetingId, request) {
        const r = await this.registerForMeetingRaw(meetingId, request);
        if (!r.data)
            throw new Error(r.message ?? 'Registration failed');
        return r.data;
    }
    registerForMeetingRaw(meetingId, request) {
        return this.http.post(this.buildUrl(this.getEndpoint('register', `meetings/${meetingId}/register`)), request);
    }
    async getLogs(filter = {}) {
        const r = await this.getLogsRaw(filter);
        return r.data ?? [];
    }
    getLogsRaw(filter = {}) {
        return this.http.get(this.buildUrlWithParams(this.getEndpoint('logs', 'logs'), {
            function: filter.function,
            failuresOnly: filter.failuresOnly || undefined,
            take: filter.take ?? 100,
        }));
    }
    // ── Dashboard + history ─────────────────────────────────────────────────────
    async getDashboard(refresh = false) {
        const r = await this.getDashboardRaw(refresh);
        if (!r.data)
            throw new Error(r.message ?? 'Could not read the Anatomy dashboard');
        return r.data;
    }
    getDashboardRaw(refresh = false) {
        return this.http.get(this.buildUrlWithParams(this.getEndpoint('dashboard', 'dashboard'), { refresh: refresh || undefined }));
    }
    async getFollowUpHistory(take = 100) {
        const r = await this.getFollowUpHistoryRaw(take);
        return r.data ?? [];
    }
    getFollowUpHistoryRaw(take = 100) {
        return this.http.get(this.buildUrlWithParams(this.getEndpoint('follow-up-history', 'follow-ups'), { take }));
    }
    async getRegistrationHistory(take = 100) {
        const r = await this.getRegistrationHistoryRaw(take);
        return r.data ?? [];
    }
    getRegistrationHistoryRaw(take = 100) {
        return this.http.get(this.buildUrlWithParams(this.getEndpoint('registrations', 'registrations'), { take }));
    }
    // ── CCMS members ↔ Anatomy ──────────────────────────────────────────────────
    /** At least two characters. Empty when MemberManagement is unreachable — the desk still works without it. */
    async searchMembers(term, limit = 20) {
        const r = await this.searchMembersRaw(term, limit);
        return r.data ?? [];
    }
    searchMembersRaw(term, limit = 20) {
        return this.http.get(this.buildUrlWithParams(this.getEndpoint('members-search', 'members/search'), { term, limit }));
    }
    async getMemberLink(memberId) {
        const r = await this.getMemberLinkRaw(memberId);
        return r.data ?? null;
    }
    getMemberLinkRaw(memberId) {
        return this.http.get(this.buildUrl(this.getEndpoint('member-link', `members/${memberId}/link`)));
    }
    /** Explicit link; `anatomyUserId: 0` clears it. Requires Anatomy.Manage. */
    async linkMember(memberId, request) {
        const r = await this.linkMemberRaw(memberId, request);
        if (!r.data)
            throw new Error(r.message ?? 'Could not link the member');
        return r.data;
    }
    linkMemberRaw(memberId, request) {
        return this.http.post(this.buildUrl(this.getEndpoint('member-link', `members/${memberId}/link`)), request);
    }
}
AnatomyService.DEFAULT_SERVICE_PATH = 'anatomy';
AnatomyService.BASE_PATH = 'api/anatomy';
//# sourceMappingURL=AnatomyService.js.map